import React, { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";

import Intercom from "@intercom/messenger-js-sdk";
import { ChevronDown, Menu, Phone, X } from "lucide-react";

import useCalApi from "@/components/hooks/useCalApi";
import { useCalendarLink } from "@/components/hooks/useCalendarLink";
import { useUser } from "@/components/hooks/useUser";
import { Button } from "@/components/ui/button";
import { INTERCOM_APP_ID, PRODUCT_CATEGORIES, PRODUCTS } from "@/lib/constants";
import { COMPANY } from "@/lib/constants/marketing-items";
import { cn } from "@/lib/utils";
import { getProductHref } from "@/lib/utils/marketing";
import { clashGrotesk, jakartaSans } from "@/styles/fonts";

import type { LucideIcon } from "lucide-react";

// Type definitions
interface NavItem {
  title: string;
  href: string;
  Icon?: LucideIcon;
}

interface NavDropdown {
  type: "dropdown";
  title: string;
  items: NavItem[];
}

interface NavLink {
  type: "link";
  title: string;
  href: string;
}

type NavConfigItem = NavDropdown | NavLink;

interface ListItemProps {
  className?: string;
  title: string;
  Icon?: LucideIcon;
  href: string;
}

interface DesktopDropdownMenuProps {
  title: string;
  items: NavItem[];
}

interface DesktopLinkProps {
  title: string;
  href: string;
}

interface MobileDropdownSectionProps {
  title: string;
  items: NavItem[];
}

interface MobileLinkProps {
  title: string;
  href: string;
}

interface NavLinksProps {
  isMobile?: boolean;
}

interface AnnouncementBannerProps {
  isAnimated: boolean;
  onClose: () => void;
}

interface FixedNavProps {
  isFixed?: boolean;
}

const createNavConfig = (): NavConfigItem[] => [
  {
    type: "dropdown",
    title: "Company",
    items: COMPANY.map((company) => ({
      title: company.name,
      href: company.slug,
      Icon: company.icon,
    })),
  },
  {
    type: "link",
    title: "Pricing",
    href: "/pricing",
  },
  {
    type: "link",
    title: "Case Studies",
    href: "/case-studies",
  },
];

const ListItem = React.forwardRef<HTMLAnchorElement, ListItemProps>(({ className, title, Icon, ...props }, ref) => {
  return (
    <li>
      <Link
        ref={ref}
        className={cn(
          "rounded-lg p-3 w-full bg-transparent hover:bg-ash-100 group transition-all duration-300 flex items-center gap-3",
          className
        )}
        {...props}
      >
        <div className="flex items-center gap-2">
          {Icon && (
            <Icon className="w-5 h-5 shrink-0 text-ash-700 group-hover:text-rebolt-500 transition-all duration-300" />
          )}

          <p className="font-medium text-md leading-[1.1] text-ash-700 group-hover:text-rebolt-500 transition-all duration-300">
            {title}
          </p>
        </div>
      </Link>
    </li>
  );
});
ListItem.displayName = "ListItem";

const DesktopDropdownMenu = ({ title, items }: DesktopDropdownMenuProps) => (
  <li className="relative group">
    <button className="group inline-flex h-10 w-max items-center text-lg justify-center rounded-md px-4 py-2 font-medium transition-colors">
      {title}
      <ChevronDown
        className="relative top-px ml-1 h-5 w-5 transition duration-200 group-hover:rotate-180"
        aria-hidden="true"
      />
    </button>
    <div className="absolute left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
      <div className="origin-top-center relative mt-1.5 overflow-hidden rounded-md border border-ash-200 bg-white text-ash-900 shadow-lg p-4">
        <ul className={cn("flex flex-col gap-2", title === "Case Studies" ? "w-[300px]" : "w-[200px]")}>
          {items.map((item) => (
            <li key={item.title}>
              <Link
                href={item.href}
                className="rounded-md w-full bg-transparent hover:bg-ash-100 group transition-all duration-300 flex items-center gap-2 p-2"
              >
                {item.Icon && <item.Icon className="w-5 h-5 shrink-0 text-ash-600 transition-all duration-300" />}
                <span className="text-base font-medium text-ash-700 transition-all duration-300">{item.title}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </li>
);

// Stripe-style Products dropdown with categorized sections
const ProductsDropdown = () => {
  const marketingProducts = PRODUCTS.filter((p) => p.category === PRODUCT_CATEGORIES.MARKETING_KIT);
  const crmProducts = PRODUCTS.filter((p) => p.category === PRODUCT_CATEGORIES.CRM);

  return (
    <li className="relative group">
      <button className="group inline-flex h-10 w-max items-center text-lg justify-center rounded-md px-4 py-2 font-medium transition-colors">
        Products
        <ChevronDown
          className="relative top-px ml-1 h-5 w-5 transition duration-200 group-hover:rotate-180"
          aria-hidden="true"
        />
      </button>
      <div className="absolute left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
        {/* Triangle pointer */}
        <div className="absolute left-1/2 -translate-x-1/2 top-2 w-0 h-0 border-l-10 border-l-transparent border-r-10 border-r-transparent border-b-10 border-b-white z-10" />
        <div className="origin-top-center relative mt-1.5 overflow-hidden rounded-xl border border-ash-200 bg-white text-ash-900 shadow-xl">
          <div className="flex min-w-[480px]">
            {/* Marketing Kit Section */}
            <div className="flex-1 p-5 border-r border-ash-100">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-ash-500 mb-3 px-2">Marketing Kit</h3>
              <ul className="flex flex-col gap-1">
                {marketingProducts.map((product) => (
                  <li key={product.productKey}>
                    <Link
                      href="/#marketing-kit"
                      className="rounded-lg w-full bg-transparent hover:bg-ash-50 group/item transition-all duration-200 flex items-start gap-3 p-2"
                    >
                      <div className="w-8 h-8 rounded-lg bg-linear-to-br from-rebolt-100 to-rebolt-200 flex items-center justify-center shrink-0 mt-0.5">
                        <product.icon className="w-4 h-4 text-rebolt-600" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-ash-800 group-hover/item:text-rebolt-600 transition-colors">
                          {product.shortName}
                        </span>
                        {product.navDescription && (
                          <span className="text-xs text-ash-500">{product.navDescription}</span>
                        )}
                      </div>
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    href="/ai-voice-assistant"
                    className="rounded-lg w-full bg-transparent hover:bg-ash-50 group/item transition-all duration-200 flex items-start gap-3 p-2"
                  >
                    <div className="w-8 h-8 rounded-lg bg-linear-to-br from-rebolt-100 to-rebolt-200 flex items-center justify-center shrink-0 mt-0.5">
                      <Phone className="w-4 h-4 text-rebolt-600" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-ash-800 group-hover/item:text-rebolt-600 transition-colors">
                        AI Voice Assistant
                      </span>
                      <span className="text-xs text-ash-500">Never miss a call again</span>
                    </div>
                  </Link>
                </li>
              </ul>
            </div>

            {/* CRM Section */}
            <div className="flex-1 p-5 bg-ash-50/50">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-ash-500 mb-3 px-2">CRM</h3>
              <ul className="flex flex-col gap-1">
                {crmProducts.map((product) => (
                  <li key={product.productKey}>
                    <Link
                      href={getProductHref(product)}
                      className={cn(
                        "rounded-lg w-full bg-transparent hover:bg-white group/item transition-all duration-200 flex items-start gap-3 p-2",
                        product.isComingSoon && "cursor-default hover:bg-transparent"
                      )}
                      onClick={(e) => product.isComingSoon && e.preventDefault()}
                    >
                      <div
                        className={cn(
                          "w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5",
                          product.isComingSoon ? "bg-ash-100" : "bg-linear-to-br from-rebolt-100 to-rebolt-200"
                        )}
                      >
                        <product.icon
                          className={cn("w-4 h-4", product.isComingSoon ? "text-ash-400" : "text-rebolt-600")}
                        />
                      </div>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span
                            className={cn(
                              "text-sm font-semibold transition-colors",
                              product.isComingSoon ? "text-ash-400" : "text-ash-800 group-hover/item:text-rebolt-600"
                            )}
                          >
                            {product.shortName}
                          </span>
                          {product.isComingSoon && (
                            <span className="text-[10px] font-medium uppercase tracking-wide text-ash-400 bg-ash-100 px-1.5 py-0.5 rounded">
                              Soon
                            </span>
                          )}
                        </div>
                        {product.navDescription && (
                          <span className={cn("text-xs", product.isComingSoon ? "text-ash-400" : "text-ash-500")}>
                            {product.navDescription}
                          </span>
                        )}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

// Reusable direct link component for desktop view
const DesktopLink = ({ title, href }: DesktopLinkProps) => (
  <li>
    <Link
      href={href}
      className="group inline-flex h-10 w-max items-center text-lg justify-center rounded-md px-4 py-2 font-medium transition-colors"
    >
      {title}
    </Link>
  </li>
);

// Reusable dropdown section for mobile view
const MobileDropdownSection = ({ title, items }: MobileDropdownSectionProps) => (
  <li>
    <span>{title}</span>
    <ul className="ml-4 mt-2 space-y-2">
      {items.map((item) => (
        <li key={item.title}>
          <Link href={item.href} className="text-sm block py-1">
            {item.title}
          </Link>
        </li>
      ))}
    </ul>
  </li>
);

// Reusable direct link component for mobile view
const MobileLink = ({ title, href }: MobileLinkProps) => (
  <li>
    <Link href={href}>{title}</Link>
  </li>
);

// Mobile Products dropdown with categorized sections
const MobileProductsSection = () => {
  const marketingProducts = PRODUCTS.filter((p) => p.category === PRODUCT_CATEGORIES.MARKETING_KIT);
  const crmProducts = PRODUCTS.filter((p) => p.category === PRODUCT_CATEGORIES.CRM);

  return (
    <li>
      <span className="text-base md:text-lg">Products</span>
      <div className="ml-4 mt-3 space-y-4">
        {/* Marketing Kit */}
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-ash-500">Marketing Kit</span>
          <ul className="mt-2 space-y-1">
            {marketingProducts.map((product) => (
              <li key={product.productKey}>
                <Link href="/#marketing-kit" className="text-sm flex items-center gap-2 py-1.5">
                  <product.icon className="w-4 h-4 text-ash-500" />
                  {product.shortName}
                </Link>
              </li>
            ))}
            <li>
              <Link href="/ai-voice-assistant" className="text-sm flex items-center gap-2 py-1.5">
                <Phone className="w-4 h-4 text-ash-500" />
                AI Voice Assistant
              </Link>
            </li>
          </ul>
        </div>
        {/* CRM */}
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-ash-500">CRM</span>
          <ul className="mt-2 space-y-1">
            {crmProducts.map((product) => (
              <li key={product.productKey}>
                <Link
                  href={getProductHref(product)}
                  className={cn("text-sm flex items-center gap-2 py-1.5", product.isComingSoon && "text-ash-400")}
                  onClick={(e) => product.isComingSoon && e.preventDefault()}
                >
                  <product.icon className={cn("w-4 h-4", product.isComingSoon ? "text-ash-300" : "text-ash-500")} />
                  {product.shortName}
                  {product.isComingSoon && (
                    <span className="text-[10px] font-medium uppercase tracking-wide text-ash-400 bg-ash-100 px-1.5 py-0.5 rounded">
                      Soon
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </li>
  );
};

const NavLinks = ({ isMobile = false }: NavLinksProps) => {
  // Create the navigation config inside the component
  const NAV_CONFIG = createNavConfig();

  if (isMobile) {
    return (
      <ul className="text-base md:text-lg text-ash-900 flex flex-col gap-6">
        <MobileProductsSection />
        {NAV_CONFIG.map((item, index) => {
          if (item.type === "dropdown") {
            return <MobileDropdownSection key={index} title={item.title} items={item.items} />;
          } else {
            return <MobileLink key={index} title={item.title} href={item.href} />;
          }
        })}
      </ul>
    );
  }

  return (
    <div className="hidden lg:block">
      <ul className="text-base md:text-lg text-ash-900 flex items-center gap-4">
        <ProductsDropdown />
        {NAV_CONFIG.map((item, index) => {
          if (item.type === "dropdown") {
            return <DesktopDropdownMenu key={index} title={item.title} items={item.items} />;
          } else {
            return <DesktopLink key={index} title={item.title} href={item.href} />;
          }
        })}
      </ul>
    </div>
  );
};

function AnnouncementBanner({ isAnimated, onClose }: AnnouncementBannerProps) {
  return (
    <div
      className={cn(
        "relative top-0 left-0 right-0 bg-rebolt-600 text-white z-50 py-3 px-4 transition-all duration-500 ease-in-out",
        isAnimated ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
      )}
    >
      <div className="mx-auto flex items-center justify-center gap-4 relative">
        <p className="font-medium">
          Announcing our $3M <span className="font-bold">seed round</span>!
        </p>
        <div className="flex items-center gap-5">
          <Button variant="outline" className="border-white text-ash-900 h-10" asChild>
            <Link href="/resources/fueling-the-future-of-the-trades-rebolt-raises-a-usd3-million-seed-round">
              Read update
            </Link>
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="text-white h-10 hover:text-white/30 hover:bg-transparent hidden xs:block absolute right-0"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function NavbarLegacy({ isFixed = true }: FixedNavProps) {
  const { user } = useUser();
  const router = useRouter();
  const [hasScrolled, setHasScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [isAnimated, setIsAnimated] = useState(false);
  useCalApi();
  const { calPath } = useCalendarLink();

  useEffect(() => {
    try {
      Intercom({
        app_id: INTERCOM_APP_ID,
      });
      if (typeof window !== "undefined" && window.Intercom) {
        window.Intercom("update", {
          hide_default_launcher: false,
        });
      }
    } catch (error) {
      console.error("Error configuring Intercom", error);
    }
  }, []);

  useEffect(() => {
    const loadMetricool = () => {
      const head = document.getElementsByTagName("head")[0];
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.src = "https://tracker.metricool.com/resources/be.js";
      script.onload = () => {
        if (typeof window !== "undefined" && (window as any).beTracker) {
          (window as any).beTracker.t({ hash: "e8dddb5a4ce88ccb1c2f0df1272651d" });
        }
      };
      head.appendChild(script);
    };
    loadMetricool();
  }, []);

  useEffect(() => {
    // Trigger animation after component mounts
    const timer = setTimeout(() => setIsAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const sentinel = document.createElement("div");
    sentinel.style.position = "absolute";
    sentinel.style.top = "20px"; // Match our scroll threshold
    sentinel.style.height = "1px";
    sentinel.style.width = "1px";
    sentinel.style.opacity = "0";
    document.body.appendChild(sentinel);

    const observer = new IntersectionObserver(
      ([entry]) => {
        setHasScrolled(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
      sentinel.remove();
    };
  }, []);

  const shouldShowBanner = !router.asPath.includes("townsquare-interactive");

  return (
    <>
      {bannerVisible && shouldShowBanner && (
        <AnnouncementBanner isAnimated={isAnimated} onClose={() => setBannerVisible(false)} />
      )}
      <nav
        className={cn(
          "z-40 transition-all duration-500 ease-in-out px-3",
          isFixed ? "fixed inset-x-0" : "absolute inset-x-0",
          bannerVisible && shouldShowBanner && isAnimated && !hasScrolled ? "top-24" : "top-0 mt-4",
          jakartaSans.variable,
          clashGrotesk.variable
        )}
      >
        <div
          className={cn(
            "w-full mx-auto flex items-center py-2 justify-between max-w-7xl",
            hasScrolled ? "px-6 rounded-full shadow-sm bg-ash-100/40 backdrop-blur-2xl" : "px-6"
          )}
        >
          <Link href="/">
            <img src="/rebolt-branding/rebolt-full-dark.svg" className="h-10" alt="Rebolt Logo" />
            <span className="sr-only">Rebolt</span>
          </Link>

          <div className="flex items-center gap-4">
            <NavLinks />
          </div>

          <div className="flex items-center gap-4">
            <ul className="lg:flex items-center gap-7 w-full hidden">
              {user?.id ? (
                <Button variant="rebolt-primary" size="lg" asChild>
                  <Link href="/dashboard">Dashboard</Link>
                </Button>
              ) : (
                <>
                  <Link href="/sign-in" className="opacity-80 font-medium">
                    Login
                  </Link>

                  <Button
                    variant="rebolt-primary"
                    size="lg"
                    data-cal-link={calPath}
                    data-cal-config='{"layout":"month_view"}'
                  >
                    Get more business
                  </Button>
                </>
              )}
            </ul>
            {/* Mobile Menu Button */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden"
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6 text-ash-900" /> : <Menu className="h-6 w-6 text-ash-900" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <div
          className={cn(
            "absolute left-3 right-3 mt-2 lg:hidden transition-all duration-200 ease-in-out origin-top max-h-[80vh] overflow-y-auto",
            mobileMenuOpen ? "opacity-100 scale-y-100" : "opacity-0 scale-y-0 pointer-events-none",
            "bg-ash-100 bg-opacity-40 backdrop-blur-2xl rounded-3xl shadow-lg"
          )}
        >
          <div className="py-6 px-6">
            <NavLinks isMobile />
            <div className="mt-6">
              <ul className="flex items-center gap-7 w-full">
                {user?.id ? (
                  <Button variant="rebolt-primary" asChild className="w-full">
                    <Link href="/dashboard">Dashboard</Link>
                  </Button>
                ) : (
                  <>
                    <Button asChild className="w-full rounded-full font-bold">
                      <Link href="/sign-in">Sign in</Link>
                    </Button>
                    <Button
                      variant="rebolt-primary"
                      className="w-full"
                      data-cal-link={calPath}
                      data-cal-config='{"layout":"month_view"}'
                    >
                      Get Started
                    </Button>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}
